import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useStaticQuery, graphql } from 'gatsby';
import Box from 'common/components/Box';
import Text from 'common/components/Text';
import Heading from 'common/components/Heading';
import {
  Accordion,
  AccordionItem,
  AccordionTitle,
  AccordionBody,
  IconWrapper,
  OpenIcon,
  CloseIcon,
} from 'common/components/Accordion';
import Container from 'common/components/UI/Container';
import { Icon } from 'react-icons-kit';
import { plus } from 'react-icons-kit/entypo/plus';
import { minus } from 'react-icons-kit/entypo/minus';

import FaqWrapper from './faq.style';
import {
  FAQ_DIFFERENCE,
  FAQ_PRO_TRIAL,
  FAQ_TRIAL_ENDS,
  FAQ_SINCE,
  FAQ_HOW_IT_WORKS,
  FAQ_STORE,
  FAQ_DATA_SENT,
  FAQ_SIGN_IN,
  FAQ_ASSISTANT,
  FAQ_LICENSE_KEY,
  FAQ_DOCS,
  FAQ_PRIVILEGES,
  FAQ_AWS_COSTS,
  FAQ_OS,
} from './faq-data';

// Longer addresses first, so the pattern matches them before the bare domain.
const LINKS = {
  'docs.cloudpouch.dev/reference/aws-permissions/':
    'https://docs.cloudpouch.dev/reference/aws-permissions/',
  'docs.cloudpouch.dev': 'https://docs.cloudpouch.dev/',
  'hello@cloudpouch.dev': 'mailto:hello@cloudpouch.dev',
};

// Renders plain FAQ text, wrapping listed snippets in the code font and
// turning known addresses into links.
const renderAnswer = (text, code = []) => {
  const tokens = [...code, ...Object.keys(LINKS)];
  const pattern = new RegExp(
    `(${tokens.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`
  );
  return text.split(pattern).map((part, i) => {
    if (LINKS[part]) {
      return (
        <a key={i} href={LINKS[part]} target='_blank' rel='noreferrer'>
          {part}
        </a>
      );
    }
    if (code.includes(part)) return <CodeFont key={i}>{part}</CodeFont>;
    return part;
  });
};

const fromData = ({ question, answer, code, expanded }) => ({
  title: question,
  ...(expanded ? { expend: true } : {}),
  description: <>{renderAnswer(answer, code)}</>,
});


const CodeFont = styled.span`
  background: rgba(0,0,0,0.1);
  color: black;
  border-radius: 5px;
  max-width: 100%;
  font-size: 80%;
  padding: 0.3em 0.25em;
  font-family: "SF Mono", SFMono-Regular, Consolas, "Liberation Mono", Menlo, Courier, monospace;
`;

const FaqSection = ({
  sectionWrapper,
  row,
  col,
  secTitleWrapper,
  secHeading,
  secText,
  title,
  description,
  buttonWrapper,
  button,
}) => {
  const Data = [
    fromData(FAQ_DIFFERENCE),
    fromData(FAQ_PRO_TRIAL),
    fromData(FAQ_TRIAL_ENDS),
    fromData(FAQ_SINCE),
    fromData(FAQ_PRIVILEGES),
    fromData(FAQ_AWS_COSTS),
    fromData(FAQ_HOW_IT_WORKS),
    fromData(FAQ_STORE),
    fromData(FAQ_DATA_SENT),
    fromData(FAQ_SIGN_IN),
    fromData(FAQ_ASSISTANT),
    fromData(FAQ_LICENSE_KEY),
    {
      title: "How do I cancel my subscription?",
      description:
        'In the email sent by Paddle.com (subject: "Welcome to CloudPouch...", from: "help@paddle.com") there is a link titled "review your subscription". Click on it and next click on "Cancel Subscription".',
    },
    fromData(FAQ_DOCS),
    fromData(FAQ_OS),
    {
      title: 'Where can I find you? Any Social Media?',
      description:(
      <>
        Check our Twitter account at <a href='https://twitter.com/CloudPouch' target='_blank'>@CloudPouch</a> and join our group on <a href='https://www.linkedin.com/groups/12588087/' target='_blank'>LinkedIn</a>.
      </>),
    },
    {
      title: 'I have a question, problem, feature request, suggestion?',
      description:(<>
        Don't hesitate to contact us. We're open to your ideas. Either write an e-mail at <a href='mailto:hello@cloudpouch.dev'>hello@cloudpouch.dev</a> or 
        visit our <a href='https://github.com/CloudPouch/CloudPouch.dev/issues' target='_blank'>GitHub Issues page</a> to create a ticket.
      </>)
      },
  ];

  return (
    <Box {...sectionWrapper} id="faq_section">
      <Container>
        <Box {...secTitleWrapper}>
          <Text {...secText} content="FREQUENTLY ASKED QUESTIONS" />
          <Heading {...secHeading} content="Want to ask something from us?" />
        </Box>
        <Box {...row}>
          <Box {...col}>
            <FaqWrapper>
              <Accordion>
                <>
                  {Data.map((accordionItem, index) => (
                    <AccordionItem
                      className="accordion_item"
                      key={`accordion-${index}`}
                      expanded={accordionItem.expend}
                    >
                      <>
                        <AccordionTitle className="accordion_title">
                          <>
                            <Heading {...title} content={accordionItem.title} />
                            <IconWrapper>
                              <OpenIcon>
                                <Icon icon={minus} size={18} />
                              </OpenIcon>
                              <CloseIcon>
                                <Icon icon={plus} size={18} />
                              </CloseIcon>
                            </IconWrapper>
                          </>
                        </AccordionTitle>
                        <AccordionBody className="accordion_body">
                          <Text
                            {...description}
                            content={accordionItem.description}
                          />
                        </AccordionBody>
                      </>
                    </AccordionItem>
                  ))}
                </>
              </Accordion>
            </FaqWrapper>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

FaqSection.propTypes = {
  sectionWrapper: PropTypes.object,
  secTitleWrapper: PropTypes.object,
  row: PropTypes.object,
  col: PropTypes.object,
  secHeading: PropTypes.object,
  secText: PropTypes.object,
  title: PropTypes.object,
  description: PropTypes.object,
  buttonWrapper: PropTypes.object,
  button: PropTypes.object,
};

FaqSection.defaultProps = {
  sectionWrapper: {
    as: 'section',
    pt: ['20px', '30px', '50px', '50px'],
    pb: ['60px', '80px', '80px', '80px'],
  },
  secTitleWrapper: {
    mb: ['55px', '65px'],
  },
  secText: {
    as: 'span',
    display: 'block',
    textAlign: 'center',
    fontSize: '14px',
    letterSpacing: '0.15em',
    fontWeight: '700',
    color: '#46679f',
    mb: '5px',
  },
  secHeading: {
    textAlign: 'center',
    fontSize: ['20px', '24px'],
    fontWeight: '500',
    color: '#0f2137',
    letterSpacing: '-0.025em',
    mb: '0',
    lineHeight: '1.67',
  },
  row: {
    flexBox: true,
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  col: {
    width: ['100%', '100%', '75%', '75%'],
  },
  title: {
    fontSize: ['16px', '19px'],
    fontWeight: '400',
    color: 'headingColor',
    letterSpacing: '-0.025em',
    mb: 0,
  },
  description: {
    fontSize: '15px',
    color: 'textColor',
    lineHeight: '1.75',
    mb: 0,
  },
  buttonWrapper: {
    mt: '50px',
    flexBox: true,
    justifyContent: 'center',
  },
  button: {
    type: 'button',
    fontSize: '14px',
    fontWeight: '600',
    borderRadius: '4px',
    pl: ['22px'],
    pr: ['22px'],
    colors: 'secondaryWithBg',
    minWidth: '150px',
  },
};

export default FaqSection;
